import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
//import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import MoreIcon from '../../../assets/icons/more-horizontal-icon.svg';
import { Insurance, InsurancePlan } from '../../../types/plans/types';
import {
  rangeToLabel,
  reconstructAgeRange,
} from '../../../utils/ageRanges';

/**
 * What to show for a plan whose friendlyName is missing.
 *
 * friendlyName is `string | null` and in practice is sometimes blank, which the
 * sort above already treats as absent. Rendering it raw left the row's only
 * control with no label at all: a screen reader announces "link" and nothing
 * else, and there is no visible text to aim a mouse at either. Falls back to the
 * insurer before giving up, so the usual case still names something real.
 */
const planDisplayName = (insurance: Insurance): string =>
  [insurance.friendlyName, insurance.companyName]
    .find((name) => name && name.trim())
    ?.toLocaleUpperCase() ?? 'UNKNOWN PLAN';
import { getStatusConfig } from '../../../utils/getStatusConfig';

interface PlanCardComponentProps {
  plans: InsurancePlan[];
  practiceId: string;
  practiceName: string;
  onEditInsurance?: () => void;
  onAddNewPlan?: () => void;
}

const PlanCardComponent: React.FC<PlanCardComponentProps> = ({
  plans,
  onEditInsurance,
  onAddNewPlan,
}) => {
  const [filteredPlans, setFilteredPlans] =
    useState<InsurancePlan[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [enhancedBadges, setEnhancedBadges] = useState(() => {
    // Load from localStorage or default to true
    try {
      const saved = localStorage.getItem('enhancedBadges');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });
  // New filter state for schedule status
  const [selectedScheduleFilter, setSelectedScheduleFilter] =
    useState<
      'All' | 'OK to Schedule' | 'Do not schedule' | 'PCP Match'
    >('All');

  // New filter state for practice
  const [selectedPracticeFilter, setSelectedPracticeFilter] =
    useState<string>('All');

  // Sort state - simplified to just plan name sorting like MarketPracticeList
  const [sortOrder, setSortOrder] = useState<
    'ascending' | 'descending'
  >('ascending');

  // Calculate counts based on getStatusConfig labels - memoized for performance
  const scheduleCounts = useMemo(() => {
    return plans.reduce(
      (acc, insurancePlan) => {
        insurancePlan.plans.forEach((plan) => {
          acc.total++;
          const statusLabel = getStatusConfig(
            plan.value,
            plan.insurance,
            undefined,
            plan.credentialstatus
          ).label;
          if (
            plan.value?.toLowerCase() === 'schedule allowed' &&
            plan.credentialstatus?.toLowerCase() !== 'pending'
          ) {
            acc.okToSchedule++;
          } else if (statusLabel === 'Do not schedule') {
            acc.doNotSchedule++;
          } else if (plan.value?.toLowerCase() === 'pcp match') {
            acc.pcpMatch++;
          }
        });
        return acc;
      },
      { total: 0, okToSchedule: 0, doNotSchedule: 0, pcpMatch: 0 }
    );
  }, [plans]);

  // Calculate practice counts - memoized for performance
  const practiceCounts = useMemo(() => {
    return plans.reduce(
      (acc, insurancePlan) => {
        const practiceId = insurancePlan.practice.id;
        const practiceName =
          insurancePlan.practice.name || 'Unknown Practice';

        if (!acc.practices[practiceId]) {
          acc.practices[practiceId] = {
            name: practiceName,
            count: 0,
          };
        }
        acc.practices[practiceId].count += insurancePlan.plans.length;
        return acc;
      },
      {
        practices: {} as Record<
          string,
          { name: string; count: number }
        >,
      }
    );
  }, [plans]);

  // Get unique practices for filter dropdown
  const uniquePractices = Object.entries(
    practiceCounts.practices
  ).map(([id, data]) => ({
    id,
    name: data.name,
    count: data.count,
  }));

  // Initialize filteredPlans when plans prop changes
  useEffect(() => {
    setFilteredPlans(plans);
  }, [plans]);

  useEffect(() => {
    let updatedPlans = [...plans]; // Create a copy to avoid mutations

    // Filter by practice
    if (selectedPracticeFilter !== 'All') {
      updatedPlans = updatedPlans.filter(
        (insurancePlan) =>
          insurancePlan.practice.id === selectedPracticeFilter
      );
    }

    // Filter by schedule status
    if (selectedScheduleFilter !== 'All') {
      updatedPlans = updatedPlans
        .map((insurancePlan) => ({
          ...insurancePlan,
          plans: insurancePlan.plans.filter((plan) => {
            const label = getStatusConfig(
              plan.value,
              plan.insurance,
              undefined,
              plan.credentialstatus
            ).label;
            if (selectedScheduleFilter === 'PCP Match') {
              return plan.value?.toLowerCase() === 'pcp match';
            }
            if (selectedScheduleFilter === 'OK to Schedule') {
              return (
                plan.value?.toLowerCase() === 'schedule allowed' &&
                plan.credentialstatus?.toLowerCase() !== 'pending'
              );
            }
            return label === selectedScheduleFilter;
          }),
        }))
        .filter((insurancePlan) => insurancePlan.plans.length > 0);
    }

    // Filter by search term
    if (searchTerm) {
      updatedPlans = updatedPlans
        .map((insurancePlan) => ({
          ...insurancePlan,
          // Matches on the name actually shown, so a plan displaying its
          // insurer's name is not invisible to a search for that name.
          plans: insurancePlan.plans.filter((plan) =>
            planDisplayName(plan.insurance)
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          ),
        }))
        .filter((insurancePlan) => insurancePlan.plans.length > 0);
    }

    // Sort plans by name like MarketPracticeList
    updatedPlans = updatedPlans.map((insurancePlan) => ({
      ...insurancePlan,
      plans: insurancePlan.plans.sort((a, b) => {
        const nameA = planDisplayName(a.insurance);
        const nameB = planDisplayName(b.insurance);
        return sortOrder === 'ascending'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }),
    }));

    setFilteredPlans(updatedPlans);
  }, [
    searchTerm,
    selectedScheduleFilter,
    selectedPracticeFilter,
    sortOrder,
    plans,
  ]);

  // Handle navigation to plan detail page - memoized to prevent unnecessary re-renders
  // Handle enhanced badges toggle - memoized to prevent unnecessary re-renders
  const handleEnhancedBadgesToggle = useCallback(() => {
    const newValue = !enhancedBadges;
    setEnhancedBadges(newValue);
    localStorage.setItem('enhancedBadges', JSON.stringify(newValue));
  }, [enhancedBadges]);

  return (
    <Box
      mb="5rem"
      p="1rem"
      mt="2rem"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="0.25rem"
    >
      {/* Plans Header with Action Menu */}
      <Flex justify="space-between" align="center" mb="1rem">
        <Text
          color="text.primary"
          fontSize="1rem"
          fontWeight={700}
          lineHeight="120%"
        >
          Plans
        </Text>
        <Flex gap="0.5rem" align="center">
          <IconButton
            aria-label="Status Legend"
            icon={<InfoIcon />}
            size="sm"
            variant="ghost"
            onClick={() => setIsLegendOpen(true)}
            color="blue.500"
            h="2.5rem"
            w="2.5rem"
            borderRadius="0.375rem"
            _hover={{
              bg: 'blue.50',
              color: 'blue.600',
              transform: 'translateY(-1px)',
              boxShadow: 'sm',
            }}
            _active={{
              bg: 'blue.100',
              transform: 'translateY(0)',
            }}
            transition="all 0.2s ease-in-out"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handleEnhancedBadgesToggle}
            color={enhancedBadges ? 'blue.600' : 'gray.600'}
            bg={enhancedBadges ? 'blue.50' : 'transparent'}
            borderColor={enhancedBadges ? 'blue.300' : 'gray.300'}
            fontWeight={enhancedBadges ? '600' : '400'}
            h="2.5rem"
            px="1rem"
            fontSize="0.875rem"
            borderRadius="0.375rem"
            _hover={{
              bg: enhancedBadges ? 'blue.100' : 'gray.50',
              borderColor: enhancedBadges ? 'blue.400' : 'gray.400',
            }}
            transition="all 0.2s ease-in-out"
            title={`Enhanced Badge Mode: ${
              enhancedBadges ? 'ON' : 'OFF'
            }`}
          >
            Enhanced Badges {enhancedBadges ? 'ON' : 'OFF'}
          </Button>

          {/* Sort Select - same design as MarketPracticeList */}
          <Select
            bg="#F7FAFC"
            width="10rem"
            height="2.5rem"
            fontSize="0.875rem"
            _hover={{ bg: 'white' }}
            _active={{
              transform: 'scale(0.98)',
            }}
            _focus={{ boxShadow: 'outline' }}
            borderRadius="0.375rem"
            border="1px solid transparent"
            value={sortOrder}
            onChange={(e) =>
              setSortOrder(
                e.target.value as 'ascending' | 'descending'
              )
            }
          >
            <option value="ascending">Plan Name A - Z</option>
            <option value="descending">Plan Name Z - A</option>
          </Select>
          {onEditInsurance && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<img src={MoreIcon} />}
                size="sm"
                aria-label="Insurance Plans Options"
                borderRadius="0.375rem"
                background={'background.secondary'}
                width="2.5rem"
                height="2.5rem"
                cursor={'pointer'}
                border="none"
                transition="all 0.2s ease-in-out"
              />
              <MenuList>
                <MenuItem onClick={onEditInsurance}>
                  Edit Insurance Plans
                </MenuItem>
                {onAddNewPlan && (
                  <MenuItem onClick={onAddNewPlan}>
                    Add New Plan
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
        </Flex>
      </Flex>
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        mb="1rem"
        gap="1rem"
      >
        <Flex direction="column" gap="0.75rem" flex="1">
          {/* Practice Filter */}
          <Flex gap="0.75rem" wrap="wrap">
            <Button
              height="1.75rem"
              padding="0.25rem 1rem"
              borderRadius="624.9375rem"
              background={
                selectedPracticeFilter === 'All'
                  ? 'background.blue100'
                  : 'none'
              }
              onClick={() => setSelectedPracticeFilter('All')}
              color={
                selectedPracticeFilter === 'All'
                  ? 'text.blue700'
                  : 'text.primary'
              }
              fontSize="0.875rem"
              fontWeight={
                selectedPracticeFilter === 'All' ? 600 : 500
              }
              lineHeight="1.25rem"
            >
              All Practices (
              {uniquePractices.reduce((sum, p) => sum + p.count, 0)})
            </Button>
            {uniquePractices.map((practice) => (
              <Button
                key={practice.id}
                height="1.75rem"
                padding="0.25rem 1rem"
                borderRadius="624.9375rem"
                background={
                  selectedPracticeFilter === practice.id
                    ? 'background.blue100'
                    : 'none'
                }
                onClick={() => setSelectedPracticeFilter(practice.id)}
                color={
                  selectedPracticeFilter === practice.id
                    ? 'text.blue700'
                    : 'text.primary'
                }
                fontSize="0.875rem"
                fontWeight={
                  selectedPracticeFilter === practice.id ? 600 : 500
                }
                lineHeight="1.25rem"
                whiteSpace="nowrap"
                title={practice.name}
                minWidth="fit-content"
                flexShrink={0}
              >
                {practice.name} ({practice.count})
              </Button>
            ))}
          </Flex>

          {/* Status Filter */}
          <Flex gap="0.75rem" wrap="wrap">
            {[
              'All',
              'OK to Schedule',
              'Do not schedule',
              'PCP Match',
            ].map((status) => (
              <Button
                key={status}
                height="1.75rem"
                padding="0.25rem 1rem"
                borderRadius="624.9375rem"
                background={
                  selectedScheduleFilter === status
                    ? 'background.blue100'
                    : 'none'
                }
                onClick={() =>
                  setSelectedScheduleFilter(
                    status as
                      | 'All'
                      | 'OK to Schedule'
                      | 'Do not schedule'
                      | 'PCP Match'
                  )
                }
                color={
                  selectedScheduleFilter === status
                    ? 'text.blue700'
                    : 'text.primary'
                }
                fontSize="0.875rem"
                fontWeight={
                  selectedScheduleFilter === status ? 600 : 500
                }
                lineHeight="1.25rem"
              >
                {status === 'All'
                  ? `All Plans (${scheduleCounts.total})`
                  : status === 'OK to Schedule'
                  ? `OK to Schedule (${scheduleCounts.okToSchedule})`
                  : status === 'Do not schedule'
                  ? `Do not schedule (${scheduleCounts.doNotSchedule})`
                  : `PCP Match (${scheduleCounts.pcpMatch})`}
              </Button>
            ))}
          </Flex>
        </Flex>

        <Input
          placeholder="Search insurance plans"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          width="300px"
          flexShrink={0}
        />
      </Flex>

      <Divider mb="1rem" />
      <Box mt="1rem">
        <Grid
          templateColumns="1fr 1.5fr 160px 180px"
          gap="0.75rem"
          alignItems="center"
          mb="0.5rem"
          px="0.5rem"
        >
          <Text fontWeight={700} fontSize="0.95rem" color="gray.600">
            Plan Name
          </Text>
          <Text fontWeight={700} fontSize="0.95rem" color="gray.600">
            Practice
          </Text>
          <Text fontWeight={700} fontSize="0.95rem" color="gray.600">
            Age Range
          </Text>
          <Text fontWeight={700} fontSize="0.95rem" color="gray.600">
            Status
          </Text>
        </Grid>
        <Stack spacing="0.62rem">
          {filteredPlans.length > 0 ? (
            filteredPlans.flatMap((insurancePlan) =>
              insurancePlan.plans.map((plan) => {
                const statusConfig = getStatusConfig(
                  plan.value,
                  plan.insurance,
                  undefined,
                  plan.credentialstatus,
                  plan.metadata
                );
                return (
                  <Grid
                    key={plan.id}
                    templateColumns="1fr 1.5fr 160px 180px"
                    gap="0.75rem"
                    alignItems="center"
                    px="0.5rem"
                    _hover={{
                      background: 'blue.50',
                      transition: 'background 0.2s',
                    }}
                    borderRadius="0.25rem"
                  >
                    {/* A real anchor, not a Text with onClick. It looks and
                        behaves like a link, so it has to be reachable by Tab,
                        activate on Enter, announce itself as a link, and open
                        in a new tab on ctrl/middle click. */}
                    <Text
                      as={RouterLink}
                      to={`/plans/${plan.insurance.id}`}
                      fontSize="1rem"
                      fontWeight={400}
                      lineHeight="150%"
                      color="blue.600"
                      // Wraps, like the practice column beside it. Clipped to a
                      // single line the name ran out in an ellipsis, and since
                      // the text is also the link, reading the rest of it and
                      // navigating away were the same click.
                      whiteSpace="normal"
                      wordBreak="break-word"
                      textDecoration="underline"
                      _hover={{
                        color: 'blue.800',
                        textDecoration: 'none',
                      }}
                      _focusVisible={{
                        outline: '2px solid',
                        outlineColor: 'blue.600',
                        outlineOffset: '2px',
                        borderRadius: '2px',
                      }}
                    >
                      {planDisplayName(plan.insurance)}
                    </Text>
                    <Text
                      fontSize="1rem"
                      fontWeight={400}
                      lineHeight="150%"
                      color="text.primary"
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      {insurancePlan.practice.name ||
                        'Unknown Practice'}
                    </Text>
                    <Text
                      fontSize="1rem"
                      fontWeight={400}
                      lineHeight="150%"
                      color="text.primary"
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      {(() => {
                        // Reconstruct age range using the same logic as ProviderInsuranceTable
                        const ageRange = reconstructAgeRange({
                          minimumAge: plan.minimumAge,
                          maximumAge: plan.maximumAge,
                          age: plan.age,
                          metadata: plan.metadata as
                            | {
                                minimumAge?: number | null;
                                maximumAge?: number | null;
                              }
                            | undefined,
                        });

                        // Use the same rangeToLabel function for consistent display
                        return rangeToLabel(ageRange);
                      })()}
                    </Text>
                    <Tooltip
                      label={statusConfig.helperText}
                      hasArrow
                      placement="top-start"
                      maxW="25rem"
                      minW="15rem"
                      offset={[0, 10]}
                    >
                      <Box
                        display="flex"
                        height={enhancedBadges ? '3.5rem' : '2.75rem'}
                        padding="1rem"
                        justifyContent="center"
                        alignItems="center"
                        gap="0.25rem"
                        width="100%"
                        maxWidth="12rem"
                        borderRadius="0.3125rem"
                        background={statusConfig.bgColor}
                        cursor="help"
                        transition="all 0.2s ease-in-out"
                        flexDirection={
                          enhancedBadges ? 'column' : 'row'
                        }
                        flexShrink={0}
                        overflow="hidden"
                        position="relative"
                        data-testid="chip"
                      >
                        <Text
                          fontSize="0.75rem"
                          fontWeight="600"
                          lineHeight="1.1"
                          color="white"
                          textAlign="left"
                          whiteSpace="normal"
                          wordBreak="break-word"
                          width="100%"
                          maxW="100%"
                          flex="0 0 auto"
                        >
                          {statusConfig.label}
                        </Text>
                        {enhancedBadges && (
                          <Text
                            fontSize="0.625rem"
                            fontWeight="400"
                            lineHeight="1.0"
                            color="white"
                            textAlign="left"
                            opacity="0.9"
                            whiteSpace="normal"
                            wordBreak="break-word"
                            width="100%"
                            maxW="100%"
                            flex="0 0 auto"
                          >
                            {statusConfig.helperText.split(' | ')[0]}
                          </Text>
                        )}
                      </Box>
                    </Tooltip>
                  </Grid>
                );
              })
            )
          ) : (
            <Text
              fontSize="1rem"
              lineHeight="1.5"
              textAlign="center"
              color="gray.500"
            >
              No plans found.
            </Text>
          )}
        </Stack>
      </Box>

      {/* Status Legend Modal */}
      <Modal
        isOpen={isLegendOpen}
        onClose={() => setIsLegendOpen(false)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Status Badge Legend & Help</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="2rem">
            <Stack spacing="1.5rem">
              <Box>
                <Text fontSize="1.125rem" fontWeight="600" mb="1rem">
                  Status Color Meanings
                </Text>
                <Stack spacing="1rem">
                  {[
                    {
                      color: 'green.500',
                      title: 'OK to Schedule',
                      subtitle:
                        'Assigned HMO PCP must be in the tax ID',
                      description:
                        'This provider is fully credentialed and can accept new patients for this insurance plan.',
                      example: 'Schedule Allowed',
                    },
                    {
                      color: 'purple.500',
                      title: 'HMO plan requires exact PCP match',
                      subtitle:
                        'Exact PCP match required for HMO plans',
                      description:
                        "HMO plans require the provider to be the exact PCP listed in the patient's plan.",
                      example: 'PCP Match',
                    },
                    {
                      color: 'blue.500',
                      title: 'Schedule Established patients',
                      subtitle: 'Panel is closed; No new patients',
                      description:
                        'Provider can only see existing patients. New patient panel is closed.',
                      example: 'Established patients only',
                    },
                    {
                      color: 'yellow.500',
                      title: 'Do not schedule',
                      subtitle:
                        'Application/credentialing is in process',
                      description:
                        "Provider's application is being processed. Wait for completion before scheduling.",
                      example: 'Pending Credentialing',
                    },
                    {
                      color: 'red.500',
                      title: 'Do not schedule',
                      subtitle: 'Provider does not take this plan',
                      description:
                        'Provider is not contracted with this insurance plan. Do not schedule patients.',
                      example: 'Not Participating',
                    },
                    {
                      color: 'orange.500',
                      title: 'Status Unknown',
                      subtitle:
                        'Contact administrator for clarification',
                      description:
                        'Status information is unclear. Contact administrator to determine scheduling eligibility.',
                      example: 'No Status',
                    },
                  ].map((item, index) => (
                    <Flex
                      key={index}
                      gap="1rem"
                      alignItems="flex-start"
                    >
                      <Box
                        w="2rem"
                        h="2rem"
                        bg={item.color}
                        borderRadius="0.375rem"
                        flexShrink={0}
                      />
                      <Box flex="1">
                        <Text fontWeight="600" color={item.color}>
                          {item.title}
                        </Text>
                        <Text
                          fontSize="0.875rem"
                          color="gray.600"
                          mb="0.25rem"
                        >
                          {item.subtitle}
                        </Text>
                        <Text
                          fontSize="0.875rem"
                          color="gray.700"
                          mb="0.25rem"
                        >
                          {item.description}
                        </Text>
                        <Text
                          fontSize="0.75rem"
                          color="gray.500"
                          fontStyle="italic"
                        >
                          Example: {item.example}
                        </Text>
                      </Box>
                    </Flex>
                  ))}
                </Stack>
              </Box>

              <Box>
                <Text
                  fontSize="1.125rem"
                  fontWeight="600"
                  mb="0.5rem"
                >
                  How to Use
                </Text>
                <Text fontSize="0.875rem" color="gray.600">
                  • Hover over any status badge to see full details
                  <br />
                  • Use the filter buttons above to view specific
                  status types
                  <br />• Contact administrator if you see "Status
                  Unknown" or unclear statuses
                </Text>
              </Box>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PlanCardComponent;
